import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Clock, 
  Zap, 
  Plus, 
  Trash2, 
  Save,
  RefreshCw,
  Twitter,
  MessageCircle,
  Facebook,
  Instagram,
  ChevronDown,
  ChevronUp,
  Settings2,
  Calendar,
  BarChart3,
  TrendingUp,
  Gamepad2,
  HelpCircle,
  Smile,
  Megaphone,
  Bell,
  Info,
  Coins,
  Trophy,
  Newspaper,
  BookOpen,
  Star,
  Gift,
  MessageSquare,
  PieChart,
  CalendarDays,
  Target,
  Users,
  Dices,
  PlusCircle,
  X,
  Film,
  Clapperboard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Engine icons and colors by category
const ENGINE_CONFIG = {
  // Crypto
  crypto_movers: { icon: Coins, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  crypto_signals: { icon: BarChart3, color: 'text-green-400', bg: 'bg-green-500/10' },
  crypto_fear_greed: { icon: TrendingUp, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  // Sports - General
  sports_lifecycle: { icon: Trophy, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  daily_tips: { icon: Target, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  player_stats: { icon: Users, color: 'text-teal-400', bg: 'bg-teal-500/10' },
  // Sports - Specific
  tennis_lifecycle: { icon: Trophy, color: 'text-lime-400', bg: 'bg-lime-500/10' },
  football_lifecycle: { icon: Trophy, color: 'text-green-400', bg: 'bg-green-500/10' },
  cricket_lifecycle: { icon: Trophy, color: 'text-sky-400', bg: 'bg-sky-500/10' },
  // Casino
  casino_trivia: { icon: Gamepad2, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  casino_news: { icon: Newspaper, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  how_to_play: { icon: BookOpen, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  // Engagement
  meme_engagement: { icon: Smile, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  quote_of_the_day: { icon: MessageSquare, color: 'text-violet-400', bg: 'bg-violet-500/10' },
  polls_quizzes: { icon: PieChart, color: 'text-sky-400', bg: 'bg-sky-500/10' },
  weekly_recap: { icon: CalendarDays, color: 'text-lime-400', bg: 'bg-lime-500/10' },
  // Fun & Lifestyle
  joke_of_the_day: { icon: Smile, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  this_day_in_history: { icon: CalendarDays, color: 'text-stone-400', bg: 'bg-stone-500/10' },
  daily_horoscope: { icon: Star, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  weather_updates: { icon: Newspaper, color: 'text-sky-400', bg: 'bg-sky-500/10' },
  random_facts: { icon: Zap, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  riddles: { icon: Gamepad2, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  movie_trivia: { icon: Film, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  gold_prices: { icon: Coins, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  festival_countdown: { icon: Gift, color: 'text-red-400', bg: 'bg-red-500/10' },
  tech_news: { icon: Zap, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  // Promo
  promotions: { icon: Megaphone, color: 'text-red-400', bg: 'bg-red-500/10' },
  announcements: { icon: Bell, color: 'text-gray-400', bg: 'bg-gray-500/10' },
  // Entertainment
  bollywood_news: { icon: Film, color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10' },
};

// Category colors
const CATEGORY_CONFIG = {
  crypto: { label: 'Crypto', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30' },
  sports: { label: 'Sports', color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30' },
  casino: { label: 'Casino', color: 'text-purple-400', bg: 'bg-purple-500/20', border: 'border-purple-500/30' },
  engagement: { label: 'Engagement', color: 'text-orange-400', bg: 'bg-orange-500/20', border: 'border-orange-500/30' },
  fun: { label: 'Fun & Lifestyle', color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30' },
  finance: { label: 'Finance', color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30' },
  news: { label: 'News', color: 'text-cyan-400', bg: 'bg-cyan-500/20', border: 'border-cyan-500/30' },
  promo: { label: 'Promotional', color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30' },
  entertainment: { label: 'Entertainment', color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/20', border: 'border-fuchsia-500/30' },
};

const PLATFORM_ICONS = {
  twitter: Twitter,
  telegram: MessageCircle,
  facebook: Facebook,
  instagram: Instagram,
  discord_webhook: MessageCircle,
  discord_bot: MessageCircle,
};

// Available posting times
const AVAILABLE_TIMES = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00',
  '20:00', '21:00', '22:00', '23:00'
];

const EngineCard = ({ engine, isAttached, onToggle, schedule, onScheduleChange, onSave, onTestPost, saving }) => {
  const config = ENGINE_CONFIG[engine.id] || { icon: Zap, color: 'text-gray-400', bg: 'bg-gray-500/10' };
  const categoryConfig = CATEGORY_CONFIG[engine.category] || CATEGORY_CONFIG.engagement;
  const Icon = config.icon;
  const [expanded, setExpanded] = useState(false);
  const [localTimes, setLocalTimes] = useState(schedule?.times || engine.default_cadence?.times || []);
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);

  useEffect(() => {
    if (schedule) {
      setLocalTimes(schedule.times || []);
      setEnabled(schedule.enabled ?? true);
    }
  }, [schedule]);

  const handleAddTime = (time) => {
    if (!localTimes.includes(time)) {
      const newTimes = [...localTimes, time].sort();
      setLocalTimes(newTimes);
    }
  };

  const handleRemoveTime = (time) => {
    setLocalTimes(localTimes.filter(t => t !== time));
  };

  const handleSave = () => {
    onSave(engine.id, { times: localTimes, enabled });
  };

  return (
    <div className={`border rounded-lg p-4 ${isAttached ? 'border-green-600/50 bg-green-900/10' : 'border-gray-700 bg-gray-800/30'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${config.bg}`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-white">{engine.name}</span>
              <Badge className={`text-xs ${categoryConfig.bg} ${categoryConfig.color} border ${categoryConfig.border}`}>
                {categoryConfig.label}
              </Badge>
              {!engine.automated && (
                <Badge variant="outline" className="text-xs border-yellow-600 text-yellow-400">Manual</Badge>
              )}
              {/* Info Tooltip */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-gray-400 hover:text-white transition-colors">
                      <Info className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-sm bg-gray-800 border-gray-600 p-3">
                    <p className="text-sm text-white font-medium mb-1">{engine.name}</p>
                    <p className="text-xs text-gray-300">{engine.what_it_posts || engine.description}</p>
                    {engine.default_cadence?.times && (
                      <p className="text-xs text-gray-400 mt-2">
                        Default times: {engine.default_cadence.times.join(', ')}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-xs text-gray-400 max-w-md">{engine.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {isAttached && engine.automated && (
            <Badge className={enabled ? 'bg-green-600' : 'bg-gray-600'}>
              {enabled ? 'Active' : 'Paused'}
            </Badge>
          )}
          <Switch
            checked={isAttached}
            onCheckedChange={(checked) => onToggle(engine.id, checked)}
          />
          {isAttached && engine.automated && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-gray-400"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Expanded schedule config */}
      {expanded && isAttached && engine.automated && (
        <div className="mt-4 pt-4 border-t border-gray-700 space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-300">Schedule Enabled</Label>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          <div>
            <Label className="text-sm text-gray-300 mb-2 block">Posting Times (IST)</Label>
            <div className="flex flex-wrap gap-2 mb-3">
              {localTimes.map(time => (
                <Badge 
                  key={time} 
                  className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
                  onClick={() => handleRemoveTime(time)}
                >
                  {time} <Trash2 className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              {localTimes.length === 0 && (
                <span className="text-sm text-gray-500">No times set</span>
              )}
            </div>
            
            <Select onValueChange={handleAddTime}>
              <SelectTrigger className="w-40 bg-gray-800 border-gray-600">
                <SelectValue placeholder="Add time..." />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                {AVAILABLE_TIMES.filter(t => !localTimes.includes(t)).map(time => (
                  <SelectItem key={time} value={time} className="text-white hover:bg-gray-700">
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button 
              onClick={() => onTestPost(engine.id)} 
              disabled={saving}
              variant="outline"
              className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Quick Test Post
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Schedule
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ChannelScheduleCard = ({ channel, engines, onUpdate, onDeleteChannel }) => {
  const [schedules, setSchedules] = useState([]);
  const [attachedEngines, setAttachedEngines] = useState(channel.attached_engines || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { token } = useAuth();

  const PlatformIcon = PLATFORM_ICONS[channel.platform] || MessageCircle;

  useEffect(() => {
    fetchSchedules();
  }, [channel.id]);

  const fetchSchedules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/channels/${channel.id}/schedules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSchedules(data.schedules || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
    setLoading(false);
  };

  const handleEngineToggle = async (engineId, attach) => {
    setSaving(true);
    try {
      let newEngines;
      if (attach) {
        newEngines = [...attachedEngines, engineId];
      } else {
        newEngines = attachedEngines.filter(e => e !== engineId);
      }

      const response = await fetch(`${API_URL}/api/admin/channels/${channel.id}/engines`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ engine_ids: newEngines })
      });

      if (response.ok) {
        setAttachedEngines(newEngines);
        toast.success(`${attach ? 'Added' : 'Removed'} ${engineId} ${attach ? 'to' : 'from'} ${channel.display_name}`);
        
        // If attaching, create default schedule
        if (attach) {
          const engine = engines.find(e => e.id === engineId);
          if (engine?.automated && engine?.default_cadence?.times) {
            await handleSaveSchedule(engineId, { 
              times: engine.default_cadence.times, 
              enabled: true 
            });
          }
        }
      }
    } catch (error) {
      toast.error('Failed to update engines');
    }
    setSaving(false);
  };

  const handleSaveSchedule = async (engineId, config) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/channels/${channel.id}/schedules/${engineId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          schedule_type: 'specific_times',
          times: config.times,
          enabled: config.enabled
        })
      });

      if (response.ok) {
        toast.success(`Schedule updated for ${engineId}`);
        fetchSchedules();
      }
    } catch (error) {
      toast.error('Failed to save schedule');
    }
    setSaving(false);
  };

  const handleTestPost = async (engineId) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/channels/${channel.id}/test-post/${engineId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message || `Test post sent for ${engineId}!`);
      } else {
        toast.error(result.message || 'Test post failed');
      }
    } catch (error) {
      toast.error('Failed to create test post');
    }
    setSaving(false);
  };

  const getScheduleForEngine = (engineId) => {
    return schedules.find(s => s.engine_id === engineId);
  };

  return (
    <Card className="border-gray-700 bg-gray-900/50">
      <CardHeader 
        className="cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PlatformIcon className="w-6 h-6 text-blue-400" />
            <div>
              <CardTitle className="text-lg">{channel.display_name}</CardTitle>
              <CardDescription className="text-sm">
                {channel.platform} • {attachedEngines.length} engines • {schedules.filter(s => s.enabled).length} active schedules
                {channel.handle && (
                  <span className="text-blue-400 ml-2">• @{channel.handle}</span>
                )}
              </CardDescription>
              <CardDescription className="text-xs text-gray-500 mt-1">
                {channel.created_by_name && `Created by ${channel.created_by_name}`}
                {channel.last_post_at && ` • Last post: ${new Date(channel.last_post_at).toLocaleDateString()}`}
                {channel.id && <span className="ml-2 font-mono text-gray-600">ID: {channel.id.slice(0, 8)}</span>}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={channel.autopilot_enabled ? 'bg-green-600' : 'bg-gray-600'}>
              {channel.autopilot_enabled ? 'Autopilot ON' : 'Autopilot OFF'}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-red-400 hover:text-red-300 hover:bg-red-900/20 p-1 h-6"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete "${channel.display_name}"?\n\nID: ${channel.id}\nHandle: ${channel.handle || 'N/A'}\n\nThis cannot be undone.`)) {
                  onDeleteChannel && onDeleteChannel(channel.id);
                }
              }}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          <div className="text-sm text-gray-400 mb-4">
            <Settings2 className="w-4 h-4 inline mr-2" />
            Toggle engines on/off and configure posting times
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {engines.filter(e => e.automated).map(engine => (
                <EngineCard
                  key={engine.id}
                  engine={engine}
                  isAttached={attachedEngines.includes(engine.id)}
                  onToggle={handleEngineToggle}
                  schedule={getScheduleForEngine(engine.id)}
                  onScheduleChange={() => {}}
                  onSave={handleSaveSchedule}
                  onTestPost={handleTestPost}
                  saving={saving}
                />
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// Create Channel Modal Component
const CreateChannelModal = ({ isOpen, onClose, brands, engines, accounts, onSuccess, token }) => {
  const [formData, setFormData] = useState({
    brand_id: '',
    platform: '',
    connected_account_id: '',
    display_name: '',
    handle: '',
    purpose: 'engagement',
    theme: 'crypto',
    autopilot_enabled: true,
    selectedEngines: []
  });
  const [creating, setCreating] = useState(false);

  // Filter accounts by selected platform
  const filteredAccounts = accounts.filter(acc => acc.platform === formData.platform);

  const handleEngineToggle = (engineId) => {
    setFormData(prev => ({
      ...prev,
      selectedEngines: prev.selectedEngines.includes(engineId)
        ? prev.selectedEngines.filter(e => e !== engineId)
        : [...prev.selectedEngines, engineId]
    }));
  };

  const handleCreate = async () => {
    if (!formData.brand_id || !formData.platform || !formData.display_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      // Step 1: Create the channel
      const channelRes = await fetch(`${API_URL}/api/channels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          brand_id: formData.brand_id,
          platform: formData.platform,
          connected_account_id: formData.connected_account_id || null,
          display_name: formData.display_name,
          handle: formData.handle || formData.display_name.toLowerCase().replace(/\s+/g, '_'),
          purpose: formData.purpose,
          theme: formData.theme,
          autopilot_enabled: formData.autopilot_enabled
        })
      });

      // Read response body only once
      const responseText = await channelRes.text();
      
      if (!channelRes.ok) {
        let errMsg = 'Failed to create channel';
        try {
          const errData = JSON.parse(responseText);
          errMsg = errData.detail || errMsg;
        } catch {
          errMsg = responseText || errMsg;
        }
        throw new Error(errMsg);
      }

      const newChannel = JSON.parse(responseText);

      // Step 2: Attach selected engines
      if (formData.selectedEngines.length > 0) {
        await fetch(`${API_URL}/api/admin/channels/${newChannel.id}/engines`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ engine_ids: formData.selectedEngines })
        });

        // Step 3: Create default schedules for each engine
        for (const engineId of formData.selectedEngines) {
          const engine = engines.find(e => e.id === engineId);
          if (engine?.automated && engine?.default_cadence?.times) {
            await fetch(`${API_URL}/api/channels/${newChannel.id}/schedules/${engineId}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                schedule_type: 'specific_times',
                times: engine.default_cadence.times,
                enabled: true
              })
            });
          }
        }
      }

      toast.success(`Channel "${formData.display_name}" created successfully!`);
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        brand_id: '',
        platform: '',
        connected_account_id: '',
        display_name: '',
        handle: '',
        purpose: 'engagement',
        theme: 'crypto',
        autopilot_enabled: true,
        selectedEngines: []
      });
    } catch (error) {
      toast.error(error.message || 'Failed to create channel');
    }
    setCreating(false);
  };

  const PlatformIcon = PLATFORM_ICONS[formData.platform] || MessageCircle;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-green-400" />
            Create New Channel
          </DialogTitle>
          <DialogDescription>
            Set up a new channel with connected account and posting engines
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Platform Selection */}
          <div className="space-y-2">
            <Label className="text-white">1. Select Platform *</Label>
            <div className="grid grid-cols-4 gap-2">
              {['twitter', 'telegram', 'facebook', 'instagram', 'discord_webhook', 'discord_bot'].map(platform => {
                const Icon = PLATFORM_ICONS[platform];
                const isSelected = formData.platform === platform;
                const displayName = platform === 'discord_webhook' ? 'Discord (Webhook)' : 
                                   platform === 'discord_bot' ? 'Discord (Bot)' : platform;
                return (
                  <button
                    key={platform}
                    onClick={() => setFormData(prev => ({ ...prev, platform, connected_account_id: '' }))}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-500/20 text-blue-400' 
                        : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs capitalize">{displayName}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Connected Account */}
          {formData.platform && (
            <div className="space-y-2">
              <Label className="text-white">2. Select Connected Account</Label>
              {filteredAccounts.length === 0 ? (
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm">
                  No {formData.platform} accounts connected. Go to Connected Accounts to add one first.
                </div>
              ) : (
                <Select 
                  value={formData.connected_account_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, connected_account_id: val }))}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600">
                    <SelectValue placeholder="Select an account..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    {filteredAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id} className="text-white hover:bg-gray-700">
                        {acc.account_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Step 3: Brand */}
          <div className="space-y-2">
            <Label className="text-white">3. Select Brand *</Label>
            <Select 
              value={formData.brand_id} 
              onValueChange={(val) => setFormData(prev => ({ ...prev, brand_id: val }))}
            >
              <SelectTrigger className="bg-gray-800 border-gray-600">
                <SelectValue placeholder="Select a brand..." />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                {brands.map(brand => (
                  <SelectItem key={brand.id} value={brand.id} className="text-white hover:bg-gray-700">
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Step 4: Channel Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">4. Channel Display Name *</Label>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="e.g., Muggle Twitter"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Handle (optional)</Label>
              <Input
                value={formData.handle}
                onChange={(e) => setFormData(prev => ({ ...prev, handle: e.target.value }))}
                placeholder="e.g., @muggle_crypto"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Step 5: Purpose & Theme */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Purpose</Label>
              <Select 
                value={formData.purpose} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, purpose: val }))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="engagement" className="text-white hover:bg-gray-700">Engagement</SelectItem>
                  <SelectItem value="educational" className="text-white hover:bg-gray-700">Educational</SelectItem>
                  <SelectItem value="announcements" className="text-white hover:bg-gray-700">Announcements</SelectItem>
                  <SelectItem value="conversion" className="text-white hover:bg-gray-700">Conversion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Theme</Label>
              <Select 
                value={formData.theme} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, theme: val }))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="crypto" className="text-white hover:bg-gray-700">Crypto</SelectItem>
                  <SelectItem value="sports" className="text-white hover:bg-gray-700">Sports</SelectItem>
                  <SelectItem value="casino" className="text-white hover:bg-gray-700">Casino</SelectItem>
                  <SelectItem value="mixed" className="text-white hover:bg-gray-700">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Autopilot Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
            <div>
              <Label className="text-white">Enable Autopilot</Label>
              <p className="text-xs text-gray-400">Auto-post based on schedules</p>
            </div>
            <Switch
              checked={formData.autopilot_enabled}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, autopilot_enabled: checked }))}
            />
          </div>

          {/* Step 6: Select Engines */}
          <div className="space-y-3">
            <Label className="text-white">5. Select Engines (optional)</Label>
            <p className="text-xs text-gray-400">Choose which content engines to attach. You can change this later.</p>
            
            <div className="max-h-48 overflow-y-auto space-y-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              {engines.filter(e => e.automated).map(engine => {
                const config = ENGINE_CONFIG[engine.id] || { icon: Zap, color: 'text-gray-400', bg: 'bg-gray-500/10' };
                const Icon = config.icon;
                const isSelected = formData.selectedEngines.includes(engine.id);
                
                return (
                  <div 
                    key={engine.id}
                    onClick={() => handleEngineToggle(engine.id)}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-green-500/20 border border-green-500/50' : 'hover:bg-gray-700/50'
                    }`}
                  >
                    <Checkbox checked={isSelected} className="pointer-events-none" />
                    <div className={`p-1.5 rounded ${config.bg}`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-white">{engine.name}</p>
                      <p className="text-xs text-gray-400">{engine.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {formData.selectedEngines.length > 0 && (
              <p className="text-xs text-green-400">
                {formData.selectedEngines.length} engine(s) selected - default schedules will be created
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-gray-600">
            Cancel
          </Button>
          <Button 
            onClick={handleCreate} 
            disabled={creating || !formData.brand_id || !formData.platform || !formData.display_name}
            className="bg-green-600 hover:bg-green-700"
          >
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Channel
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ScheduleManager() {
  const { token, user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [engines, setEngines] = useState([]);
  const [brands, setBrands] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewBrandModal, setShowNewBrandModal] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [creatingBrand, setCreatingBrand] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [channelsRes, enginesRes, brandsRes, accountsRes] = await Promise.all([
        fetch(`${API_URL}/api/channels`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/engines`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/brands`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      const [channelsData, enginesData, brandsData, accountsData] = await Promise.all([
        channelsRes.json(),
        enginesRes.json(),
        brandsRes.json(),
        accountsRes.json()
      ]);

      setChannels(channelsData);
      setEngines(enginesData);
      setBrands(brandsData);
      setAccounts(accountsData);
    } catch (error) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleDeleteChannel = async (channelId) => {
    try {
      const response = await fetch(`${API_URL}/api/channels/${channelId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Channel deleted successfully');
        fetchData(); // Refresh the list
      } else {
        const errorText = await response.text();
        try {
          const error = JSON.parse(errorText);
          toast.error(error.detail || 'Failed to delete channel');
        } catch {
          toast.error('Failed to delete channel');
        }
      }
    } catch (error) {
      toast.error('Failed to delete channel');
    }
  };

  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) {
      toast.error('Please enter a brand name');
      return;
    }
    
    setCreatingBrand(true);
    try {
      const response = await fetch(`${API_URL}/api/brands`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newBrandName.trim(),
          description: `${newBrandName.trim()} brand`
        })
      });
      
      if (response.ok) {
        toast.success(`Brand "${newBrandName}" created successfully!`);
        setNewBrandName('');
        setShowNewBrandModal(false);
        fetchData(); // Refresh brands list
      } else {
        const errorText = await response.text();
        try {
          const error = JSON.parse(errorText);
          toast.error(error.detail || 'Failed to create brand');
        } catch {
          toast.error('Failed to create brand');
        }
      }
    } catch (error) {
      toast.error('Failed to create brand');
    } finally {
      setCreatingBrand(false);
    }
  };

  const filteredChannels = selectedBrand === 'all' 
    ? channels 
    : channels.filter(c => c.brand_id === selectedBrand);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="schedule-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Schedule Manager</h1>
          <p className="text-gray-400">Configure engines and posting schedules for each channel</p>
        </div>

        <div className="flex items-center gap-4">
          <Select value={selectedBrand} onValueChange={setSelectedBrand}>
            <SelectTrigger className="w-48 bg-gray-800 border-gray-600">
              <SelectValue placeholder="Filter by brand" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-600">
              <SelectItem value="all" className="text-white hover:bg-gray-700">All Brands</SelectItem>
              {brands.map(brand => (
                <SelectItem key={brand.id} value={brand.id} className="text-white hover:bg-gray-700">
                  {brand.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={() => setShowNewBrandModal(true)} variant="outline" className="border-purple-600 text-purple-400 hover:bg-purple-600/20">
            <PlusCircle className="w-4 h-4 mr-2" />
            New Brand
          </Button>

          <Button onClick={fetchData} variant="outline" className="border-gray-600">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>

          <Button onClick={() => setShowCreateModal(true)} className="bg-green-600 hover:bg-green-700">
            <PlusCircle className="w-4 h-4 mr-2" />
            Create Channel
          </Button>
        </div>
      </div>

      {/* Create Channel Modal */}
      <CreateChannelModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        brands={brands}
        engines={engines}
        accounts={accounts}
        onSuccess={fetchData}
        token={token}
      />

      {/* New Brand Modal */}
      <Dialog open={showNewBrandModal} onOpenChange={setShowNewBrandModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-purple-400" />
              Create New Brand
            </DialogTitle>
            <DialogDescription>
              Add a new brand to organize your channels
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white">Brand Name *</Label>
              <Input
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                placeholder="e.g., My New Brand"
                className="bg-gray-800 border-gray-600 text-white"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateBrand()}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewBrandModal(false)} className="border-gray-600">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBrand} 
              disabled={creatingBrand || !newBrandName.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingBrand ? 'Creating...' : 'Create Brand'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Engine Legend by Category */}
      <Card className="border-gray-700 bg-gray-900/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Available Engines
            <span className="text-sm font-normal text-gray-400">({engines.filter(e => e.automated).length} automated)</span>
          </CardTitle>
          <CardDescription>Hover over ℹ️ icon on each engine to see what it posts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Group by category */}
          {['crypto', 'sports', 'casino', 'engagement', 'entertainment', 'promo'].map(category => {
            const categoryEngines = engines.filter(e => e.automated && e.category === category);
            const catConfig = CATEGORY_CONFIG[category];
            if (categoryEngines.length === 0) return null;
            
            return (
              <div key={category}>
                <div className={`text-xs font-semibold ${catConfig.color} mb-2 uppercase tracking-wide`}>
                  {catConfig.label}
                </div>
                <div className="flex flex-wrap gap-2">
                  {categoryEngines.map(engine => {
                    const config = ENGINE_CONFIG[engine.id] || { icon: Zap, color: 'text-gray-400', bg: 'bg-gray-500/10' };
                    const Icon = config.icon;
                    return (
                      <TooltipProvider key={engine.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.bg} cursor-help border border-transparent hover:border-gray-600 transition-colors`}>
                              <Icon className={`w-4 h-4 ${config.color}`} />
                              <span className="text-sm text-white">{engine.name}</span>
                              <Info className="w-3 h-3 text-gray-500" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-sm bg-gray-800 border-gray-600 p-3">
                            <p className="text-sm text-white font-medium mb-1">{engine.name}</p>
                            <p className="text-xs text-gray-300">{engine.what_it_posts || engine.description}</p>
                            {engine.default_cadence?.times && (
                              <p className="text-xs text-gray-400 mt-2">
                                <Clock className="w-3 h-3 inline mr-1" />
                                Default: {engine.default_cadence.times.join(', ')} IST
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Channels */}
      <div className="space-y-4">
        {filteredChannels.length === 0 ? (
          <Card className="border-gray-700 bg-gray-900/50">
            <CardContent className="py-12 text-center text-gray-400">
              No channels found. Create channels first to configure schedules.
            </CardContent>
          </Card>
        ) : (
          filteredChannels.map(channel => (
            <ChannelScheduleCard
              key={channel.id}
              channel={channel}
              engines={engines}
              onUpdate={fetchData}
              onDeleteChannel={handleDeleteChannel}
            />
          ))
        )}
      </div>
    </div>
  );
}
